import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress, Tab, Tabs } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import AdsSummary, { closureOptionsOf, formatMoney } from "../AdsSummary";
import PlatformLogo from "../PlatformLogo";

const PLATFORM_STORAGE_KEY = "adCampaignsPlatform";
const CURRENCY_STORAGE_KEY = "adCampaignsCurrency";
const MAX_AGE_DAYS = 60;

const readStorage = (key, fallback, allowed) => {
  try {
    const value = localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const useStyles = makeStyles()((theme) => {
  const border = `1px solid ${theme.palette.divider}`;

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    head: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    title: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      fontSize: 16,
      fontWeight: 600,
    },
    tabs: {
      minHeight: 34,
    },
    tab: {
      minHeight: 34,
      minWidth: 0,
      padding: theme.spacing(0, 1.5),
      gap: theme.spacing(0.75),
      textTransform: "none",
      fontWeight: 600,
      fontSize: 13,
    },
    list: {
      border,
      borderRadius: 12,
      overflow: "hidden",
    },
    listBody: {
      maxHeight: 360,
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    clickable: {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    result: {
      display: "flex",
      flexWrap: "wrap",
      columnGap: theme.spacing(1.25),
      rowGap: 2,
      marginTop: 3,
    },
    resultItem: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
    },
    resultLink: {
      cursor: "pointer",
      borderRadius: 4,
      "&:hover": {
        color: theme.palette.text.primary,
        textDecoration: "underline",
      },
    },
    dot: {
      width: 7,
      height: 7,
      borderRadius: "50%",
      flexShrink: 0,
    },
    row: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 96px 56px 96px",
      alignItems: "start",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 2),
      fontSize: 13,
      "& + &": {
        borderTop: border,
      },
      "& > span:not(:first-of-type)": {
        textAlign: "right",
        whiteSpace: "nowrap",
      },
    },
    rowHead: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    name: {
      display: "block",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontWeight: 500,
    },
    money: {
      fontWeight: 600,
    },
    muted: {
      color: theme.palette.text.disabled,
    },
    empty: {
      padding: theme.spacing(2),
      fontSize: 13,
      color: theme.palette.text.secondary,
      textAlign: "center",
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(3),
    },
  };
});

const AdsPanel = ({ dateFrom, dateTo, onOpenDetail }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const [platform, setPlatform] = useState(() =>
    readStorage(PLATFORM_STORAGE_KEY, "meta", ["meta", "google"])
  );
  const [currency] = useState(() => readStorage(CURRENCY_STORAGE_KEY, "USD", ["USD", "COP"]));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    let cancelado = false;

    (async () => {
      setLoading(true);
      try {
        const { data: res } = await api.get("/ticket/ad-campaigns/report", {
          params: { platform, currency, dateFrom, dateTo, maxAgeDays: MAX_AGE_DAYS },
        });
        if (!cancelado) setData(res);
      } catch (err) {
        if (!cancelado) setData(null);
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [platform, currency, dateFrom, dateTo]);

  const cambiarPlataforma = (e, value) => {
    if (!value) return;
    setPlatform(value);
    try {
      localStorage.setItem(PLATFORM_STORAGE_KEY, value);
    } catch {
    }
  };

  const campanas = (data?.campanas || []).filter(
    (c) => c.tipo !== "post" && (c.total > 0 || c.spend > 0)
  );

  const cierres = closureOptionsOf(data?.totales || {});

  const nombreDe = (c) => c.campaignName || c.adTitle || c.adReferralSourceId;

  const abrir = (c, cierre) => {
    if (!onOpenDetail) return;
    onOpenDetail({
      adId: c.adReferralSourceId,
      category: cierre ? (cierre.key.startsWith("__") ? "" : cierre.key) : undefined,
      label: cierre ? `${nombreDe(c)}  ·  ${cierre.name}` : nombreDe(c),
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <div className={classes.title}>
          <CampaignOutlinedIcon fontSize="small" />
          Anuncios
        </div>
        <Tabs value={platform} onChange={cambiarPlataforma} className={classes.tabs}>
          <Tab value="meta" icon={<PlatformLogo platform="meta" size={16} />} iconPosition="start" label="Meta" className={classes.tab} />
          <Tab value="google" icon={<PlatformLogo platform="google" size={16} />} iconPosition="start" label="Google Ads" className={classes.tab} />
        </Tabs>
        <Button
          size="small"
          className="no-imprimir"
          endIcon={<ArrowForwardIcon fontSize="small" />}
          onClick={() => navigate("/ad-campaigns")}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Ver reporte
        </Button>
      </div>

      {loading && !data ? (
        <div className={classes.loading}>
          <CircularProgress size={22} />
        </div>
      ) : (
        <>
          <AdsSummary totales={data?.totales || {}} platform={platform} compact />

          <div className={classes.list}>
            <div className={`${classes.row} ${classes.rowHead}`}>
              <span>Campaña</span>
              <span>Invertido</span>
              <span>Tickets</span>
              <span>Costo/ticket</span>
            </div>
            {campanas.length === 0 ? (
              <div className={classes.empty}>
                Sin gasto ni tickets de {platform === "google" ? "Google Ads" : "Meta"} en este periodo.
              </div>
            ) : (
              <div className={classes.listBody}>
                {campanas.map((c) => {
                  const enCurso = (c.abiertos || 0) + (c.pendientes || 0);
                  const resultado = cierres.filter((k) => c.cierres?.[k.key]);
                  return (
                    <div
                      key={c.adReferralSourceId}
                      className={`${classes.row} ${onOpenDetail && c.total ? classes.clickable : ""}`}
                      onClick={c.total ? () => abrir(c) : undefined}
                      title={c.total ? "Ver los tickets de esta campaña" : undefined}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span className={classes.name} title={nombreDe(c)}>
                          {nombreDe(c)}
                        </span>
                        {(enCurso > 0 || resultado.length > 0) && (
                          <span className={classes.result}>
                            {enCurso > 0 && <span className={classes.resultItem}>{enCurso} en curso</span>}
                            {resultado.map((k) => (
                              <span
                                key={k.key}
                                className={`${classes.resultItem} ${classes.resultLink}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  abrir(c, k);
                                }}
                                title={`Ver los tickets cerrados como "${k.name}"`}
                              >
                                <span className={classes.dot} style={{ backgroundColor: k.color }} />
                                {c.cierres[k.key]} {k.name}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>
                      <span className={c.spend != null ? classes.money : classes.muted}>
                        {c.spend != null ? formatMoney(c.spend, c.currency) : "—"}
                      </span>
                      <span>{c.total}</span>
                      <span className={c.costPerTicket != null ? undefined : classes.muted}>
                        {c.costPerTicket != null ? formatMoney(c.costPerTicket, c.currency) : "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdsPanel;
